/**
 * A failed call to the agentgateway-keys backend. Carries the backend's
 * `reason` code and `requestId` so the UI can show both: the user can quote
 * the request id to a platform operator, who finds the matching log line
 * (every backend log line for the request carries the same `requestId`).
 *
 * `message` already ends with the reference (`… (reason: key_exists,
 * request id: 1f2e…)`), so every place that renders `error.message` — toasts,
 * error panels — shows it without extra wiring. `detail` is the bare server
 * message for places that render the reference separately.
 */
export class AgentgatewayApiError extends Error {
  readonly response: Response;
  readonly status: number;
  /** Short title from the backend, e.g. `Key already exists`. */
  readonly title?: string;
  /** The bare server message, without the reason / request id suffix. */
  readonly detail: string;
  /** Stable machine-readable reason, e.g. `key_exists`, `kubernetes_forbidden`. */
  readonly reason?: string;
  readonly requestId?: string;

  constructor(options: {
    response: Response;
    title?: string;
    detail: string;
    reason?: string;
    requestId?: string;
  }) {
    super(withReference(options.detail, options.reason, options.requestId));
    this.name = 'AgentgatewayApiError';
    this.response = options.response;
    this.status = options.response.status;
    this.title = options.title;
    this.detail = options.detail;
    this.reason = options.reason;
    this.requestId = options.requestId;
  }

  /**
   * Builds the error from a non-ok response. Understands the plugin's own
   * body (`{ error, message, reason, requestId }`) and Backstage's standard
   * error body (`{ error: { name, message } }`, e.g. from the auth layer),
   * falling back to the status line. The request id falls back to the
   * `x-request-id` header.
   */
  static async fromResponse(response: Response): Promise<AgentgatewayApiError> {
    let body: any;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    const plugin = body && typeof body.error === 'string' ? body : undefined;
    const backstage =
      body && body.error && typeof body.error === 'object' ? body.error : undefined;
    const detail =
      (typeof plugin?.message === 'string' && plugin.message) ||
      (typeof backstage?.message === 'string' && backstage.message) ||
      plugin?.error ||
      `Request failed with ${response.status} ${response.statusText}`.trim();
    return new AgentgatewayApiError({
      response,
      title: plugin?.error ?? backstage?.name,
      detail,
      reason: typeof plugin?.reason === 'string' ? plugin.reason : undefined,
      requestId:
        (typeof plugin?.requestId === 'string' && plugin.requestId) ||
        response.headers.get('x-request-id') ||
        undefined,
    });
  }
}

function withReference(
  detail: string,
  reason?: string,
  requestId?: string,
): string {
  const parts = [
    reason && `reason: ${reason}`,
    requestId && `request id: ${requestId}`,
  ].filter(Boolean);
  return parts.length ? `${detail} (${parts.join(', ')})` : detail;
}
